<script setup lang="ts">
import { DeviceDetailPage } from '@/components/DeviceComponents'
import deviceConfig from '@/services/deviceConfig.service'
import store from '@/store'
import { EDeviceActions } from '@/store/deviceStore/DeviceStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { defaultCreateDevice } from '@/types/models/DeviceCreateModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { DeviceTemplateKeysModel } from '@/types/models/DeviceTemplateKeysModel'
import { EAdminPerm } from '@/types/permissions/AdminPermissionsEnum'
import { isEqual } from 'lodash'
import { computed, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { EDeviceStatus } from '../../../types/enum/DeviceStatusEnum'
import { addToQuery } from '../../../utils/queryUtils'
import DeviceApiKeyTypography from './components/DeviceApiKeyTypography.vue'
import DeviceTypeActionComponent from './components/DeviceTypeActionComponent.vue'

type Props = {
    device?: Partial<DeviceModel>
    tabsMultilined?: boolean
    isOutlookMode?: boolean
}
const props = defineProps<Props>()

const route = useRoute()
const deviceStore = ref(store.state.device.device)

const localDeviceId = computed(() => {
    if (route.query.preview) return parseInt(route.query.preview.toString())
    if (props.device && props.device.id) return props.device.device_id ?? props.device.id
    return route.params.device_id || route.params.id
        ? parseInt((route.params.device_id ?? route.params.id).toString())
        : undefined
})

const data = ref<Partial<DeviceModel>>({ ...defaultCreateDevice })
const templateKeys = ref<DeviceTemplateKeysModel[]>([])
const getDeviceConfig = (deviceId: number) =>
    deviceConfig
        .getDeviceTypeConfigByVersion(deviceId)
        .then(res => (templateKeys.value = res.data?.config_keys ?? []))

const getDevice = () =>
    store
        .dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.FETCH_DEVICE}`, {
            deviceId: localDeviceId.value,
            noDocumentTitle: props.isOutlookMode
        })
        .then(res => {
            data.value = { ...res }
            deviceStore.value = { ...res }
            if (props.isOutlookMode) addToQuery({ preview: res.device_id ?? res.id })
            if (res.device_type_id && localDeviceId.value)
                return getDeviceConfig(localDeviceId.value)
            templateKeys.value = []
        })

watch(
    () => localDeviceId.value,
    (newDeviceId, oldDeviceId) => {
        if (newDeviceId && newDeviceId !== oldDeviceId) getDevice()
    },
    { immediate: true }
)

const save = () => {
    if (!isEqual(data.value, deviceStore.value))
        store
            .dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.UPDATE_DEVICE}`, {
                deviceId: localDeviceId.value,
                deviceData: { ...data.value, roles: ['device'] }
            })
            .then(res => {
                data.value = { ...data.value, ...res }
                deviceStore.value = { ...data.value, ...res }
            })
}
const updateDeviceValue = (value: Partial<DeviceModel>) => (data.value = value)

const deviceStatus = computed(() => ({
    battery: props.device?.battery ?? deviceStore.value?.battery,
    plugged: props.device?.plugged ?? deviceStore.value?.plugged,
    signal: props.device?.signal ?? deviceStore.value?.signal,
    status: (props.device?.status as EDeviceStatus) ?? (deviceStore.value?.status as EDeviceStatus),
    network_type: props.device?.network_type ?? deviceStore.value?.network_type
}))

provide('isAdmin', ref(true))
</script>

<template>
    <DeviceDetailPage
        :storedData="deviceStore"
        :data="data"
        :status="deviceStatus"
        :templateKeys="templateKeys"
        :editPermission="EAdminPerm.ADMIN_DEVICES"
        noDetectionLink
        @save="save"
        @update="updateDeviceValue"
        :loading="store.state.device.isLoading">
        <template #informationsContent>
            <DeviceApiKeyTypography
                v-if="deviceStore?.device_id || deviceStore?.id"
                :deviceId="deviceStore?.device_id ?? deviceStore?.id" />
        </template>
        <template #actions>
            <DeviceTypeActionComponent :data="data" @refresh="getDevice()" />
        </template>
    </DeviceDetailPage>
</template>
